import { observable, action, computed } from "mobx";
import i18n from "i18n";
import { FirebaseDocument } from "common/models/FirebaseDocument";
import { createLibraryModelFromDocument, Library } from "./models/Library";
import * as _ from "lodash";
import { WBSStore } from "modules/WBS/WBSStore";
import { ModelMapStore } from "modules/modelMap/ModelMapStore";
import { ProjectStore } from "modules/project/ProjectStore";
import { Render3dService } from "modules/render3D/Render3dService";
import { LoginStore } from "modules/login/LoginStore";
import { DevelopmentStore } from "modules/development/DevelopmentStore";
import { DevelopmentReference } from "modules/development/models/DevelopmentReference";
import { TakeOffStore } from "modules/takeOff/TakeOffStore";
import { Render3dStore } from "modules/render3D/Render3dStore";
import { LibraryService } from "modules/library/LibraryService";
import { convertByteToKb } from "utils/MathUtils";
import { sortString } from "utils/StringUtils";

/**
 * Store de arquivos 3D.
 * @category Files
 * @hideconstructor
 */
class LibraryStore {
  @observable librarySelected?: FirebaseDocument<Library>;
  @observable libraries: FirebaseDocument<Library>[] = new Array();
  @observable libraryUploading: any = [];
  @observable cancel: boolean = false;
  @observable snack: boolean = false;
  @observable snackMessage: string;
  @observable changeVisibilty: boolean = false;
  @observable progressStatusUpload: any = [];
  private developmentSeletedId: string = "";

  resetStore(clearLibrary: boolean = false) {
    this.cancel = false;
    this.snack = false;
    this.snackMessage = "";
    if (clearLibrary) {
      this.libraryUploading = [] as any;
      this.progressStatusUpload = [] as any;
      this.changeVisibilty = false;
      this.librarySelected = undefined;
      this.libraries = new Array();
      this.developmentSeletedId = "";
      if (LoginStore.filesSnapshot) LoginStore.filesSnapshot();
    }
  }

  @action setLibrary(lib) {
    const ind = _.findIndex(this.libraries, function (val) {
      return val.id == lib.id;
    });
    if (ind >= 0) {
      this.libraries[ind].data.enabled = lib.data.enabled;
    }
  }

  @action
  setChangeVisibilty(value: boolean) {
    this.changeVisibilty = value;
  }

  @computed get hasActive3DModels(): boolean {
    const isXeokit =
      DevelopmentStore.developmentSelected.data.isXeokitViewer || false;
    if (isXeokit) {
      return Boolean(this.libraries3DEnabled.length);
    }

    return this.libraries.some((e) => Boolean(e.data.enabled));
  }

  @computed get libraries3DEnabled() {
    let array = new Array<string>();
    if (WBSStore.WBS !== undefined) {
      const libraries = this.libraries;
      libraries.forEach(function (value) {
        if (value.data.enabled) array.push(`sw_${value.id}`);
      });
    }
    return array;
  }

  getLibrariesLabelName(file: FirebaseDocument<Library>) {
    let name = file.data.realFileName ? file.data.realFileName : file.data.name;
    return name
      .replace(file.data.development ? file.data.development.id : "", "")
      .split(".")[0];
  }

  /** Obtém os arquivos do projeto com snapshot e atualiza a lista de libraries.
   * Quando um documento é removido da coleção, o sistema remove o arquivo do visualizador
   * e do indexedDB.
   * Nesta função também é atualizado o projeto para isXeokitViewer caso este tenha modelos
   * e não seja xeokit.
   * @async
   */
  async fetchLibraries(callback?: any): Promise<void> {
    // Verificação para evitar que o código seja chamado desnecessariamente
    if (this.developmentSeletedId === DevelopmentStore.developmentId) {
      if (callback) {
        callback();
        callback = null;
      }
      return;
    }

    this.developmentSeletedId = DevelopmentStore.developmentId;
    await LibraryService.fetchLibrariesFromDataBase(
      DevelopmentStore.developmentId,
      async (snapshot) => {
        const { developmentSelected } = DevelopmentStore;
        const oldLibraries = [...this.libraries];

        this.libraries = snapshot.docs
          .map((doc: any) => {
            const library = createLibraryModelFromDocument(doc);
            if (library.data.name.indexOf(this.developmentSeletedId) < 0) {
              console.log("Snapshot do projeto:", developmentSelected);
              console.log("Modelo da biblioteca:", library);
            }
            return library;
          })
          .filter(
            (l: FirebaseDocument<Library>) => l && l.data.status !== "deleted"
          )
          .sort((a, b) => {
            // ordena por status de importação primeiro
            if (a.data.isImportFinished !== b.data.isImportFinished)
              return a.data.isImportFinished === false ? -1 : 1;
            // depois por nome
            return sortString(a.data.name, b.data.name, true);
          });

        if (
          this.libraries.length == 0 &&
          !developmentSelected.data.isXeokitViewer
        ) {
          await LibraryService.updateLibrariesToXeokit(developmentSelected.id);
          developmentSelected.data.isXeokitViewer = true;
        }

        for (const change of snapshot.docChanges()) {
          const library = await createLibraryModelFromDocument(change.doc);

          // O tratamento que era realizado no momento da exclusão do arquivo da biblioteca passa a ser executado ao
          // remover um documento do snapshot em client
          if (change.type === "removed") {
            await LibraryService.setLibraryRender3D(library);
            await Render3dService.removeWexbimStorage(
              developmentSelected,
              library.data.name
            );
            this.showSnackLibraryFilesUpdated();
          }

          if (change.type === "modified") {
            const oldLibrary = oldLibraries.find(
              (lib) => lib.id === library.id
            );
            if (
              oldLibrary &&
              library.data.isImportFinished !== oldLibrary.data.isImportFinished
            ) {
              this.showSnackLibraryFilesUpdated();
            }
          }
        }

        if (callback) {
          callback();
          callback = null;
        }
      }
    );
  }

  private showSnackLibraryFilesUpdated() {
    if (!ProjectStore.showSnack && ModelMapStore.modelMaps.length > 0) {
      ProjectStore.showSnackMessage(
        i18n.modelMaps.menuLateral.libraryUpdated,
        i18n.modelMaps.menuLateral.libraryUpdated2
      );
    }
  }

  /** Procura o modelo com id na lista de modelos da Store. */
  findLibrayById(id: string) {
    return this.libraries.find((lib) => lib.id === id);
  }

  findLibrayByLabelName(name: string) {
    return this.libraries.find((lib) => {
      let labelName = lib.data.realFileName
        ? lib.data.realFileName
        : lib.data.name;
      labelName = labelName
        .replace(lib.data.development ? lib.data.development.id : "", "")
        .split(".")[0];
      return labelName === name;
    });
  }

  async getOnlyExistingLibraries(
    allfiles: boolean,
    ids: string[]
  ): Promise<string[]> {
    if (!allfiles) {
      return this.libraries
        .filter((lib) => ids.includes(lib.id))
        .map((lib) => lib.id);
    }
    return this.libraries.map((lib) => lib.id);
  }

  /** Altera a flag changeVisibility para disparar alterações no componente LibraryGrid caso esteja visível
   * e executa as funções necessárias para atualizar os dados de elementos visíveis no
   * Visualizador 3D.
   * @category Libraries
   */
  async changeLibrariesVisibility() {
    this.setChangeVisibilty(true);
    WBSStore.verifyLibs();
    await Render3dStore.fetchIfcItemsAndSetVisibilityByTypes();
  }

  handleFetchInVisibilityChange = async () => {
    let libOff = this.libraries.filter((e) => e.data && e.data.enabled);
    // Render3dStore.itemsView = []
    for (let libOffIndex of libOff) {
      Render3dService.fetchFileNavigation(libOffIndex.id);
    }
  };

  /**
   * Valida e inicia o upload dos arquivos selecionados.
   * @param {File[]} files - Lista de arquivos que serão upados
   */
  async uploadLibraryFiles(files: File[]) {
    const dev = DevelopmentStore.developmentSelected;
    const user = await DevelopmentStore.getDevelomentsOwner();
    const devRef = new DevelopmentReference(dev.id, "", user ? user.uid : "");

    if (!dev.data.isXeokitViewer) {
      this.showMessage(i18n.library.message.errorNotXeokitProject);
    }

    if (user) {
      for (let i = 0; i < files.length; i++) {
        if (this.validateFile(files[i], user.trial, user.totalFileSize)) {
          user.totalFileSize += convertByteToKb(files[i].size);
          await this.insertFileHandler(files[i], devRef);
        }
      }
    }
  }

  /**
   * Valida os dados do arquivo passado.
   * @param {File} file - Arquivo a ser validado
   * @param {boolean} trial - Se o usuário atual é trial, esse parâmetro será utilizado com o abaixo para validar o espaço consumido pelo usuário.
   * @param {number} totalFileSize - Total de espaço utilizado pelo usuário atual
   */
  private validateFile(
    file: File,
    trial: boolean = false,
    totalFileSize: number = 0
  ): boolean {
    const allowedExtensions = /(\.IFC)$/i;
    if (!allowedExtensions.exec(file.name)) {
      this.showMessage(i18n.library.message.errorUploadFormat);
      return false;
    }

    if (file.size > 800000000) {
      this.showMessage("O tamanho do arquivo deve ser menor que 800MB");
      return false;
    }

    if (trial && totalFileSize + convertByteToKb(file.size) > 256000) {
      DevelopmentStore.showTrialMessageError(true);
      return false;
    }

    const devId = DevelopmentStore.developmentSelected.id;
    const fileName = devId + file.name;

    if (this.libraries.find((lib) => lib.data.realFileName === fileName)) {
      this.showMessage(i18n.library.message.duplicateIFC);
      return false;
    }

    const fileUploading = this.libraryUploading.find(
      (lib) => devId + lib.name === fileName
    );
    if (fileUploading) {
      this.showMessage(i18n.library.message.duplicateIFC);
      return false;
    }

    return true;
  }

  /**
   * Inicia o upload do arquivo passado.
   * @param {File} file - Arquivos que será upado
   * @param {DevelopmentReference} dev - Dados do projeto atual
   */
  private async insertFileHandler(file: File, dev: DevelopmentReference) {
    this.libraryUploading.push({
      name: file.name,
      loading: 0,
      savedName: DevelopmentStore.developmentSelected.id + file.name,
    });
    this.cancel = false;
    try {
      await LibraryService.AddLibrary(file, dev);
      TakeOffStore.takeOffFormatedList = [];
    } catch (e) {
      console.log("exception", e);
      this.showMessage(i18n.library.message.errorUpload);
    }
  }

  private showMessage(message: string) {
    this.snackMessage = message;
    this.snack = true;
  }
}

const store = new LibraryStore();
export { store as LibraryStore };



// WEBPACK FOOTER //
// ./src/modules/library/LibraryStore.ts